import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../../config/theme';
import Typography from '@material-ui/core/Typography';
import SvgIcon from '@material-ui/core/SvgIcon';
import SelectionBox from '../Campaign/SelectionBox';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookie from 'js-cookie';
import { numberAsCurrency, minPurchasableTickets, initialTicketSelection } from '../../utils/index';
import CloseIcon from '@material-ui/icons/Close';
import { trackEvent } from '../../utils';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '14px',
    border: '1px solid #CFEBD0',
    backgroundColor: '#EEF4FF',
    padding: '16px 14px',
    marginBottom: '16px',
    textAlign: 'center',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '2px',
  },
  closeIcon: {
    color: '#5A6170',
    fontSize: '1.2rem',
  },
  ticketIconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '6px',
  },
  ticketIcon: {
    fontSize: '2rem',
    color: '#2BB7D6',
    fill: '#2BB7D6',
    transform: 'rotate(-15deg)',
  },
  titleMain: {
    fontSize: '1.2rem',
    lineHeight: 1.2,
    color: '#152C16',
    marginBottom: '6px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.2rem',
    },
  },
  titleStrong: {
    display: 'block',
    fontWeight: 800,
  },
  noticeBody: {
    fontSize: '0.82rem',
    color: '#2D5F2E',
    lineHeight: 1.45,
    marginBottom: '10px',
  },
  noticeBodyStrong: {
    fontWeight: 800,
  },
  subtitle: {
    fontSize: '0.84rem',
    color: '#1D461E',
    lineHeight: 1.4,
    fontWeight: 700,
    marginBottom: '12px',
  },
}));

export default function OrderRepurchaseSection({ order, currentUser, influencerUsername, competedForInstant }) {
  const classes = useStyles();
  const history = useHistory();
  const campaign = order?.campaign;
  const [purchaseLoading, setPurchaseLoading] = React.useState(false);
  const [selectedTickets, setSelectedTickets] = React.useState(() => initialTicketSelection(campaign));

  React.useEffect(() => {
    if (!campaign) return;
    setSelectedTickets(initialTicketSelection(campaign));
  }, [campaign?.id]);

  if (!campaign?.buttons_array?.length || !campaign['open?']) return null;

  const username = influencerUsername || campaign.influencer_username;

  function minTickets() {
    return minPurchasableTickets(campaign);
  }

  function maxTickets() {
    return campaign.max_amount === 0 ? 1000000 : campaign.max_amount;
  }

  function safeSetSelectedTickets(value) {
    if (value >= minTickets() && value <= maxTickets()) {
      setSelectedTickets(value);
    }
  }

  function purchaseCallback() {
    if (currentUser == null) {
      Cookie.set(
        'redirect_to',
        `/${username}/site/campanhas/${campaign.id}?tickets=${selectedTickets}`,
        { path: '/', expires: 1 }
      );
      window.location.href = '/public/sign_in';
      return;
    }
    setPurchaseLoading(true);
    Swal.fire({
      title: 'Efetuando a compra',
      didOpen: () => {
        Swal.showLoading();
      },
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
    });

    axios
      .post('/api/v1/purchase_orders', {
        purchase_order: {
          campaign_id: campaign.id,
          tickets_amount: selectedTickets,
        },
      })
      .then((response) => {
        trackEvent(
          'InitiateCheckout',
          {
            value: parseFloat(response.data?.total_value) || 0,
            currency: 'BRL',
            ...(response.data?.affiliate_name && { affiliate_name: response.data.affiliate_name })
          },
          { dedupe: false }
        );
        Swal.close();
        history.push(`/${username}/site/compras/${response.data.id}`);
      })
      .catch((error) => {
        let errorMessages;
        try {
          errorMessages = Object.values(error.response.data).join('<br/>');
        } catch (e) {
          errorMessages = 'Erro desconhecido';
        }
        Swal.fire({
          title: 'Erro ao efetuar a compra',
          html: errorMessages,
          icon: 'error',
        });
      })
      .finally(() => {
        setPurchaseLoading(false);
      });
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <div className={classes.topBar}>
          <CloseIcon className={classes.closeIcon} />
        </div>
        <div className={classes.ticketIconWrap}>
          <SvgIcon className={classes.ticketIcon} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 8h2v8H5zm7 0H9c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h3c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1zm-1 6h-1v-4h1v4zm7-6h-3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h3c.55 0 1-.45 1-1V9c0-.55-.45-1-1-1zm-1 6h-1v-4h1v4z" />
            <path d="M2 4v16h20V4H2zm2 14V6h16v12H4z" />
          </SvgIcon>
        </div>
        {competedForInstant ? (
          <>
            <Typography component="div" className={classes.titleMain}>
              Dessa vez você
              <span className={classes.titleStrong}>não foi premiado nos prêmios instantâneos!</span>
            </Typography>
            <Typography component="div" className={classes.noticeBody}>
              Seus bilhetes <span className={classes.noticeBodyStrong}>concorreram aos prêmios instantâneos</span>, mas não foram sorteados desta vez. Fica tranquilo: você continua concorrendo ao sorteio principal!
            </Typography>
          </>
        ) : (
          <>
            <Typography component="div" className={classes.titleMain}>
              Sua compra está
              <span className={classes.titleStrong}>concorrendo ao sorteio principal!</span>
            </Typography>
            <Typography component="div" className={classes.noticeBody}>
              Fica tranquilo: <span className={classes.noticeBodyStrong}>você está participando e concorrendo ao prêmio principal da campanha!</span>
            </Typography>
          </>
        )}
        <Typography component="div" className={classes.subtitle}>
          Você também pode garantir novos números e aumentar ainda mais suas chances!
        </Typography>
        <SelectionBox
          selectedTickets={selectedTickets}
          setSelectedTickets={safeSetSelectedTickets}
          purchaseCallback={purchaseCallback}
          campaign={campaign}
          loading={purchaseLoading}
        />
      </div>
    </ThemeProvider>
  );
}
